import { type ReactNode } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/use-translation"

interface DrawerShellProps {
  title?: string
  sourceName?: string
  idLine?: string
  onClose: () => void
  footer: ReactNode
  children: ReactNode
  /** Dims the body and makes it inert, for the step where only the footer is left to act on. */
  dimBody?: boolean
}

export function DrawerShell({ title, sourceName, idLine, onClose, footer, children, dimBody }: DrawerShellProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col bg-white overflow-hidden h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          {title
            ? <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            : <div className="h-6 w-40 bg-gray-100 rounded animate-pulse" />}
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("closeDetailsPanel")} className="h-8 w-8">
            <X size={16} />
          </Button>
        </div>
        {sourceName
          ? <p className="text-xs text-gray-400 mt-1">{t("fromSource", { sourceName })}</p>
          : <div className="h-3 mt-2 w-28 bg-gray-100 rounded animate-pulse" />}
        {idLine !== undefined
          ? <div className="flex items-center gap-3 mt-2"><span className="text-sm font-mono text-gray-500">{idLine}</span></div>
          : <div className="h-3.5 mt-2 w-24 bg-gray-100 rounded animate-pulse" />}
      </div>

      {/* Body. The inert attribute takes it out of the mouse, the keyboard and the accessibility tree at
          once, which opacity alone would not. */}
      <div
        className={`flex-1 min-h-0 overflow-y-auto${dimBody ? " opacity-40" : ""}`}
        inert={dimBody || undefined}
      >
        {children}
      </div>

      {/* Footer. Bounded as a backstop: it carries the issue panel as well as the buttons, and without a
          ceiling a long panel grows it past the drawer, squeezing the body to nothing and taking Proceed and
          Cancel off screen. What keeps the footer short is the panel itself, which opens closed and caps its
          own list; this catches whatever else ends up here, and leaves the body at least half the drawer. */}
      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-white max-h-[50vh] overflow-y-auto">
        {footer}
      </div>
    </div>
  )
}
